// ===========================================================================
// ReHome Shelter — Shelter Dashboard Page (S3)
//
// Shelter admin view: stats, staff management, quick actions.
// Only shows data for the current user's shelter (via RLS + shelter_id).
// ===========================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth, useShelterAnimals, useShelter, supabase, useShelterPolicy, upsertShelterPolicy, ADOPTER_PROFILE_FIELDS, toast } from '@rehome/shared';
import {
  PawPrint, Users, Building2, TrendingUp, Plus,
  ClipboardList, HeartHandshake, ScanLine, Loader2,
  AlertCircle, CheckCircle, X, UserPlus, Trash2, Shield,
  Mail, MapPin, FileCheck, ChevronDown, ChevronUp, Save,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────

interface StaffMember {
  id: string;
  userId: string;
  shelterId: string;
  role: string;
  permissions: Record<string, string>;
  userEmail?: string;
  userRole?: string;
}

// ─── Shelter Dashboard ────────────────────────────────────────────────────

const ShelterDashboardPage: React.FC = () => {
  const { shelterId: paramShelterId } = useParams<{ shelterId: string }>();
  const { user, userProfile } = useAuth();
  const { data: shelterAnimals } = useShelterAnimals(paramShelterId);
  const { shelter } = useShelter(paramShelterId);

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyFields, setPolicyFields] = useState<string[]>([]);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const { data: shelterPolicy } = useShelterPolicy(paramShelterId);

  // Fetch staff members for this shelter
  const fetchStaff = useCallback(async () => {
    if (!paramShelterId) return;
    setStaffLoading(true);

    // Get shelter_staff for this shelter
    const { data: staffData, error } = await supabase
      .from('shelter_staff')
      .select('id, user_id, shelter_id, role, permissions')
      .eq('shelter_id', paramShelterId);

    if (error) {
      console.warn('Failed to fetch staff:', error.message);
      setStaffLoading(false);
      return;
    }

    // Get user emails + platform role from auth.users (via user_profiles).
    // The super_admin flag lives on user_profiles.role — shelter_staff.role
    // is only ever 'Staff' or 'Admin' (see 001_initial_schema.sql).
    const userIds = (staffData ?? []).map(s => s.user_id);
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, role')
      .in('id', userIds);

    const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

    setStaff(
      (staffData ?? []).map(s => ({
        id: s.id,
        userId: s.user_id,
        shelterId: s.shelter_id,
        role: s.role,
        permissions: s.permissions as Record<string, string>,
        userEmail: profileMap.get(s.user_id)?.full_name || '',
        userRole: profileMap.get(s.user_id)?.role,
      }))
    );
    setStaffLoading(false);
  }, [paramShelterId]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    if (shelterPolicy?.defaultRequiredFields) {
      setPolicyFields(shelterPolicy.defaultRequiredFields);
    }
  }, [shelterPolicy]);

  const handleTogglePolicyField = (key: string) => {
    setPolicyFields(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  };

  const handleSavePolicy = async () => {
    if (!paramShelterId) return;
    setSavingPolicy(true);
    await upsertShelterPolicy({ shelterId: paramShelterId, defaultRequiredFields: policyFields });
    setSavingPolicy(false);
  };

  const handleDeleteStaff = async (staffId: string) => {
    if (!confirm('Biztosan eltávolítod ezt a munkatársat?')) return;
    const { error } = await supabase.from('shelter_staff').delete().eq('id', staffId);
    if (error) {
      toast.error('Nem sikerült eltávolítani a munkatársat: ' + error.message);
      return;
    }
    toast.success('Munkatárs eltávolítva.');
    fetchStaff();
  };

  const policyByCategory: Record<string, Array<typeof ADOPTER_PROFILE_FIELDS[number]>> = {};
  ADOPTER_PROFILE_FIELDS.forEach(f => {
    if (!policyByCategory[f.category]) policyByCategory[f.category] = [];
    policyByCategory[f.category].push(f);
  });

  // Staff-level role lives in shelter_staff (Admin/Staff + permissions),
  // user_profiles.role only distinguishes user/shelter_admin/super_admin.
  const myStaffRecord = staff.find(s => s.userId === user?.id);
  const isAdmin = userProfile?.role === 'super_admin' || myStaffRecord?.role === 'Admin';
  const canManageStaff = isAdmin || myStaffRecord?.permissions?.staff === 'write';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        {/* A menhely neve a címben: több menhelyet kezelő dolgozónak eddig
            semmi nem mutatta, MELYIK menhely vezérlőpultját nézi. Amíg a név
            betölt, az általános cím áll — így nem ugrik a fejléc. */}
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-teal-600" />
          {shelter?.name ? `${shelter.name} Vezérlőpult` : 'Menhely Vezérlőpult'}
        </h1>
        <p className="text-gray-600 mt-1">
          {shelterAnimals.length} állat gondozás alatt
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          icon={<PawPrint className="w-6 h-6" />}
          label="Gondozott Állatok"
          value={shelterAnimals.length.toString()}
        />
        <StatCard
          icon={<Users className="w-6 h-6" />}
          label="Munkatársak"
          value={staff.length.toString()}
        />
        <StatCard
          icon={<HeartHandshake className="w-6 h-6" />}
          label="Örökbefogadás Alatt"
          value={shelterAnimals.filter(a => a.status === 'AdoptionPending').length.toString()}
        />
        <StatCard
          icon={<ScanLine className="w-6 h-6" />}
          label="Új Bekerülések (hónap)"
          value={shelterAnimals.filter(a => {
            const d = new Date(a.createdAt);
            const now = new Date();
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          }).length.toString()}
        />
      </div>

      {/* Quick Actions + Staff Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            Gyorsműveletek
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <QuickAction to="intake" icon={<ScanLine className="w-5 h-5" />} label="Bekerülés rögzítése" color="teal" />
            <QuickAction to="animals" icon={<ClipboardList className="w-5 h-5" />} label="Állatok listája" color="blue" />
            <QuickAction to="add" icon={<Plus className="w-5 h-5" />} label="Gyors felvétel" color="green" />
            <QuickAction to="match" icon={<HeartHandshake className="w-5 h-5" />} label="Smart Match" color="purple" />
            <QuickAction to="finance" icon={<TrendingUp className="w-5 h-5" />} label="Pénzügyek" color="emerald" />
            <QuickAction to="notifications" icon={<Bell className="w-5 h-5" />} label="Értesítések" color="orange" />
          </div>
        </div>

        {/* Staff Management */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-600" />
              Munkatársak ({staff.length})
            </h2>
            {canManageStaff && (
              <button
                onClick={() => setShowAddStaff(true)}
                className="text-sm bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center gap-1.5"
              >
                <UserPlus className="w-4 h-4" />
                Hozzáadás
              </button>
            )}
          </div>

          {staffLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
            </div>
          ) : staff.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Még nincs munkatárs hozzáadva.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {staff.map(member => (
                <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{member.userEmail || 'Ismeretlen'}</p>
                      <p className="text-xs text-gray-500">
                        {member.role}
                        {Object.keys(member.permissions).length > 0 && (
                          <span className="ml-2 text-teal-600">
                            ({Object.entries(member.permissions)
                              .filter(([_, v]) => v === 'write')
                              .map(([k]) => k)
                              .join(', ')})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {canManageStaff && (() => {
                    const isSuperAdminMember = member.userRole === 'super_admin';
                    const isLastStaffMember = staff.length === 1;
                    const blockedReason = isSuperAdminMember
                      ? 'Szuperadmin nem távolítható el a menhelytől.'
                      : isLastStaffMember
                      ? 'Ő az egyetlen munkatárs, nem távolítható el.'
                      : null;

                    return (
                      <button
                        onClick={() => handleDeleteStaff(member.id)}
                        disabled={!!blockedReason}
                        className={
                          blockedReason
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-400 hover:text-red-600 transition-colors'
                        }
                        title={blockedReason || 'Eltávolítás'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Shelter Adoption Requirements */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <button
          onClick={() => setShowPolicy(prev => !prev)}
          className="flex items-center justify-between w-full text-left"
        >
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-teal-600" />
            Örökbefogadási követelmények (alapértelmezett)
            {policyFields.length > 0 && (
              <span className="text-xs font-normal bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                {policyFields.length} mező megkövetelve
              </span>
            )}
          </h2>
          {showPolicy ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {showPolicy && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
            <p className="text-sm text-gray-500">
              Itt állíthatod be, hogy a menhelyed milyen adatokat kér kötelezően az örökbefogadóktól.
              Ezek az alapértelmezett beállítások — az egyes állatoknál felülbírálhatók.
            </p>

            {Object.entries(policyByCategory).map(([category, fields]) => (
              <div key={category}>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {fields.map(field => (
                    <label
                      key={field.key}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        policyFields.includes(field.key)
                          ? 'border-teal-300 bg-teal-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={policyFields.includes(field.key)}
                        onChange={() => handleTogglePolicyField(field.key)}
                        className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{field.label}</p>
                        {field.sensitive && (
                          <span className="text-[10px] text-amber-600 font-medium">Szenzitív adat</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-end pt-3 border-t border-gray-100">
              <button
                onClick={handleSavePolicy}
                disabled={savingPolicy}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {savingPolicy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Alapértelmezések mentése
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAddStaff && (
        <AddStaffModal
          shelterId={paramShelterId!}
          onClose={() => setShowAddStaff(false)}
          onAdded={fetchStaff}
        />
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────

const StatCard: React.FC<{
  icon: React.ReactNode; label: string; value: string;
}> = ({ icon, label, value }) => (
  <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
    <div className="p-3 rounded-full bg-teal-100 text-teal-700">{icon}</div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

// Static class map — Tailwind cannot generate dynamically composed classes.
const QUICK_ACTION_STYLES: Record<string, string> = {
  teal: 'bg-teal-50 text-teal-700 hover:bg-teal-100',
  blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
  green: 'bg-green-50 text-green-700 hover:bg-green-100',
  purple: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
  emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  orange: 'bg-orange-50 text-orange-700 hover:bg-orange-100',
};

const QuickAction: React.FC<{
  to: string; icon: React.ReactNode; label: string; color: string;
}> = ({ to, icon, label, color }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-colors ${QUICK_ACTION_STYLES[color] ?? QUICK_ACTION_STYLES.teal}`}
  >
    {icon}
    {label}
  </Link>
);

// Bell icon component (inline for simplicity)
const Bell: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// ─── Add Staff Modal ────────────────────────────────────────────────────

const AddStaffModal: React.FC<{
  shelterId: string;
  onClose: () => void;
  onAdded: () => void;
}> = ({ shelterId, onClose, onAdded }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Staff');
  const [permissions, setPermissions] = useState({
    animals: 'read',
    finance: 'read',
    notifications: 'read',
    staff: 'none',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * A létrehozott fiók belépési adatai.
   *
   * MIÉRT ITT: a create-shelter-admin Edge Function generál egy ideiglenes
   * jelszót és vissza is adja, de e-mailt NEM küld róla (l. a függvényben a
   * "TODO: Send email with credentials"). Ha ezt nem mutatjuk meg, az új
   * kolléga olyan fiókot kap, amibe senki nem tud belépni. Amíg nincs
   * e-mail-kiküldés, ez az EGYETLEN hely, ahol az adat előjön — ezért a
   * párbeszédablak nem is záródik be magától.
   */
  const [credentials, setCredentials] = useState<{ email: string; tempPassword: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Call the create-shelter-admin Edge Function
      const { data, error: fnError } = await supabase.functions.invoke('create-shelter-admin', {
        body: { email, shelterId, role, permissions },
      });

      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);

      setCredentials({ email: data?.email ?? email, tempPassword: data?.tempPassword ?? '' });
      onAdded();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a munkatárs létrehozása során.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(
        `Belépés: ${credentials.email}\nIdeiglenes jelszó: ${credentials.tempPassword}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Vágólap megtagadva (jogosultság/HTTP) — az adat a képernyőn látszik,
      // kézzel is kimásolható, ezért nem csinálunk belőle hibát.
    }
  };

  const togglePermission = (key: string) => {
    setPermissions(prev => {
      const cycle: Record<string, string> = { none: 'read', read: 'write', write: 'none' };
      const current = (prev as Record<string, string>)[key] || 'none';
      return { ...prev, [key]: cycle[current] };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Munkatárs Hozzáadása</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {credentials ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>A fiók létrejött. Add át az alábbi belépési adatokat a kollégának.</span>
            </div>

            {/* Ez az egyetlen alkalom, amikor az ideiglenes jelszó látszik:
                a rendszer nem küld róla e-mailt, és visszakérdezni sem lehet. */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              <p className="font-semibold">Írd ki most — később már nem jeleníthető meg.</p>
              <p className="text-amber-800/80 text-xs mt-0.5">
                A rendszer jelenleg nem küld e-mailt a belépési adatokról. Ha elveszne, a kolléga a
                bejelentkezésnél az „Elfelejtett jelszó?” funkcióval tud újat kérni.
              </p>
            </div>

            <div className="space-y-2">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</span>
                <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm break-all">
                  {credentials.email}
                </code>
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ideiglenes jelszó</span>
                <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono break-all">
                  {credentials.tempPassword || '— a függvény nem adott vissza jelszót —'}
                </code>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Első belépéskor a rendszer kötelezően új jelszó beállítására viszi
              (<code>password_change_required</code>).
            </p>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleCopy}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50">
                {copied ? 'Kimásolva ✓' : 'Adatok másolása'}
              </button>
              <button type="button" onClick={onClose}
                className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700">
                Kész
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email cím *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email" required value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder="munkatars@menhely.hu"
                autoComplete="off"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Szerepkör
            </label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="Staff">Munkatárs</option>
              <option value="Admin">Adminisztrátor</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jogosultságok
            </label>
            <div className="space-y-2">
              {Object.entries(permissions).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700 capitalize">{key}</span>
                  <button
                    type="button"
                    onClick={() => togglePermission(key)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                      value === 'write'
                        ? 'bg-green-100 text-green-700'
                        : value === 'read'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {value === 'none' ? 'Nincs' : value === 'read' ? 'Olvasás' : 'Írás'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50">
              Mégse
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Hozzáadás
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default ShelterDashboardPage;
